import React, {useContext, useMemo, useRef, useState} from 'react';
import {useFormContext} from "react-hook-form";
import useFieldArrayClean from "../../../../../hooks/use-field-array-clean.js";
import {useQuery} from "@tanstack/react-query";
import {FaCheck, FaPen, FaPlus, FaTrash} from "react-icons/fa";
import {Card} from "../../../../../components/card/card.jsx";
import Button from "../../../../../components/button/button.jsx";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import FormField from "../../../../../components/react-hook-form/form-field.jsx";
import {MaskDefault} from "../../../../../utils/mask.js";
import InputFile from "../../../../../components/input/input-file.jsx";
import {BoFormContext} from "../context/bo-form-context.jsx";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import {BoKeys} from "../../resources/bo-keys.js";
import optionsUtils from "../../../../../utils/options-utils.js";
import {getCities, getCountries, getNeighborhoods, getStreets} from "../../../../../utils/location-utils.js";
import InputDateTime from "../../../../../components/input/input-datetime.jsx";
import InputCheckbox from "../../../../../components/input/input-checkbox.jsx";
import InputTextarea from "../../../../../components/input/input-textarea.jsx";
import {Modal} from "../../../../../components/modal/modal.jsx";
import projectUtils from "../../../../../utils/project-utils.js";
import useHasPermission from "../../../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../../../utils/enums/permission-enum.js";
import {useLoaderData} from "react-router";
import {getAssetPath} from "../../../../../utils/get-asset-path.js";
import individualService from "../../../../../services/web/individual-service.js";
import ReviseTextButton from "../../../../../components/revise-text/revise-text-button.jsx";
import cortexService from "../../../../../services/web/cortex-service.js";
import InputSwitch from "../../../../../components/input/input-switch.jsx";
import {format} from "date-fns";
import {normalizeCoordinate} from "../../../individual/form/resources/map-utils.js";
import ModalReverseGeocoding from "../../../../../components/modal/modal-reverse-geocoding.jsx";
import natureService from "../../../../../services/web/nature-service.js";
import Dropzone from "../../../../../components/dropzone/dropzone.jsx";
import ToggleButton from "../../../../../components/button/toggle-button.jsx";

const sexOptions = [
    {label: BoKeys.MALE, value: "MALE"},
    {label: BoKeys.FEMALE, value: "FEMALE"},
];

const maritalStatusOptions = [
    {label: BoKeys.MARRIED, value: "MARRIED"},
    {label: BoKeys.DIVORCED, value: "DIVORCED"},
    {label: BoKeys.SEPARATED, value: "SEPARATED"},
    {label: BoKeys.SINGLE, value: "SINGLE"},
    {label: BoKeys.STABLE_UNION, value: "STABLE_UNION"},
    {label: BoKeys.WIDOWER, value: "WIDOWER"},
];

const cnhCategoriesOptions = [
    {label: "A", value: "A"},
    {label: "B", value: "B"},
    {label: "C", value: "C"},
    {label: "D", value: "D"},
    {label: "E", value: "E"},
    {label: "ACC", value: "ACC"},
];

const emptyIndividual = (itemId) => ({
    itemId,
    __isNew: true,
    attachs: [],
    conditions: [],
    referral: [],
    cnhCategories: [],
    address: {
        date: null,
        lat: null,
        lng: null,
    },
    companyAddress: {},
    individualVersion: [],
    mentalDisorder: false,
    alcoholDrugDependency: false,
    height: "",
    bodyShape: "",
    eyes: "",
    hairType: "",
    hairColor: "",
    hairSize: "",
    roleCrime: "",
    situation: "",
    socialNetwork: "",
    information: "",
    phoneProvider: null,
    individualRegionWrapper: [],
    articles: [],
});

const emptyIndividualRegion = (itemId) => ({
    itemId,
    local: "",
    type: "",
    description: "",
});

const getNextIndividualRegionItemId = (items = []) => String(items.reduce((largest, item) => {
    const value = Number.parseInt(item?.itemId, 10);
    return Number.isFinite(value) ? Math.max(largest, value) : largest;
}, 0) + 1);

const normalizeImportedIndividualRegions = (data) => {
    const items = Array.isArray(data?.individualRegionWrapper)
        ? data.individualRegionWrapper.map((item) => ({...item, itemId: item?.itemId?.toString()}))
        : [];

    if (typeof data?.tattoo !== "string" || !data.tattoo.trim()) return items;
    if (items.some((item) => item?.type === "TATUAGEM" && item?.description === data.tattoo)) return items;

    return [...items, {
        itemId: getNextIndividualRegionItemId(items),
        local: null,
        type: "TATUAGEM",
        description: data.tattoo,
    }];
};

const emptyArticle = (itemId) => ({
    itemId,
    date: null,
    bo: "",
    article: "",
    attachId: null,
});

const getNextArticleItemId = (items = []) => String(items.reduce((largest, item) => {
    const value = Number.parseInt(item?.itemId, 10);
    return Number.isFinite(value) ? Math.max(largest, value) : largest;
}, 0) + 1);

const normalizeMultiValue = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (!value) return [];
    return [value];
};

const mapLegacyMaritalStatus = (value) => {
    const normalized = projectUtils.removeAccents((value ?? "").toUpperCase());

    const map = {
        "CASADO(A)": "MARRIED",
        "DIVORCIADO(A)": "DIVORCED",
        "SEPARADO(A)": "SEPARATED",
        "SOLTEIRO(A)": "SINGLE",
        "UNIAO ESTAVEL": "STABLE_UNION",
        "VIUVO(A)": "WIDOWER",
    };

    return map[normalized] ?? value ?? "";
};

const getCurrentVersion = (versions = []) => versions?.length ? versions[versions.length - 1] : {};

const buildMultiValue = (value, options = []) => {
    const normalized = normalizeMultiValue(value);
    const mergedOptions = [...options];

    normalized.forEach((item) => {
        if (!mergedOptions.some((option) => option.value === item)) {
            mergedOptions.push({label: item, value: item});
        }
    });

    return mergedOptions.filter((option) => normalized.includes(option.value));
};

const BoFormIndividual = () => {
    const {control, setValue, watch} = useFormContext();
    const {isSync, conditions, referrals, setIsLoading, verifySyncUrl} = useContext(BoFormContext);

    const hasPermission = useHasPermission();
    const loaderData = useLoaderData();

    const loadedCpfRef = useRef({});
    const nextIndividualRegionItemIdRef = useRef({});
    const nextArticleItemIdRef = useRef({});
    const versionTextRef = useRef({});

    const {fields, append, remove} = useFieldArrayClean({
        control,
        name: "individualList"
    });

    const [editingPersistedVersionIndexes, setEditingPersistedVersionIndexes] = useState([]);
    const [addressMap, setAddressMap] = useState({open: false, index: null});

    const [individualRegionModal, setIndividualRegionModal] = useState({
        open: false,
        individualIndex: null,
        regionIndex: null,
        allowNullLocal: false,
        data: emptyIndividualRegion("1"),
    });

    const [articleModal, setArticleModal] = useState({
        open: false,
        individualIndex: null,
        articleIndex: null,
        data: emptyArticle("1"),
        pendingAttach: null,
    });

    const closeArticleModal = () => setArticleModal({
        open: false,
        individualIndex: null,
        articleIndex: null,
        data: emptyArticle("1"),
        pendingAttach: null,
    });

    const openNewArticle = (individualIndex) => {
        const articles = watch(`individualList.${individualIndex}.articles`) ?? [];
        const individualItemId = watch(`individualList.${individualIndex}.itemId`) ?? individualIndex;
        const calculatedItemId = Number(getNextArticleItemId(articles));
        const itemId = String(Math.max(calculatedItemId, nextArticleItemIdRef.current[individualItemId] ?? 1));
        nextArticleItemIdRef.current[individualItemId] = Number(itemId) + 1;
        setArticleModal({
            open: true,
            individualIndex,
            articleIndex: null,
            data: emptyArticle(itemId),
            pendingAttach: null,
        });
    };

    const openEditArticle = (individualIndex, articleIndex, data) => setArticleModal({
        open: true,
        individualIndex,
        articleIndex,
        data: {...data},
        pendingAttach: null,
    });

    const isAttachReferencedElsewhere = (individual, attachId, excludedArticleIndex) => {
        if (!attachId) return false;
        if (individual.relatedPhotoFaceId === attachId || individual.documentPhotoId === attachId) return true;
        if (individual.individualVersion?.some((version) => version.signatureId === attachId)) return true;
        return individual.articles?.some((article, index) => index !== excludedArticleIndex && article.attachId === attachId);
    };

    const removeExclusiveTemporaryAttach = (individualIndex, articleIndex, attachId, attachs) => {
        const individual = watch(`individualList.${individualIndex}`) ?? {};
        const attach = attachs.find((item) => item.attachId === attachId);
        if (!attach?.file || isAttachReferencedElsewhere(individual, attachId, articleIndex)) return attachs;
        return attachs.filter((item) => item.attachId !== attachId);
    };

    const saveArticle = () => {
        const {individualIndex, articleIndex, pendingAttach} = articleModal;
        if (individualIndex === null) return;

        if (!articleModal.data.article) {
            projectUtils.swalErrorMessage("", "O campo \"Natureza\" é obrigatório.", "Ok");
            return;
        }

        if (articleModal.data.date && articleModal.data.date > format(new Date(), "yyyy-MM-dd")) {
            projectUtils.swalErrorMessage("", "A data não pode ser maior que a data de hoje.", "Ok");
            return;
        }

        const articlesPath = `individualList.${individualIndex}.articles`;
        const attachsPath = `individualList.${individualIndex}.attachs`;
        const articles = watch(articlesPath) ?? [];
        let attachs = watch(attachsPath) ?? [];
        const previousAttachId = articleIndex === null ? null : articles[articleIndex]?.attachId;
        const data = {...articleModal.data};

        if (pendingAttach) {
            attachs = removeExclusiveTemporaryAttach(individualIndex, articleIndex, previousAttachId, attachs);
            attachs = [...attachs.filter((attach) => attach.attachId !== pendingAttach.attachId), pendingAttach];
            data.attachId = pendingAttach.attachId;
        } else if (!data.attachId && previousAttachId) {
            attachs = removeExclusiveTemporaryAttach(individualIndex, articleIndex, previousAttachId, attachs);
        }

        const nextArticles = articleIndex === null
            ? [...articles, data]
            : articles.map((article, index) => index === articleIndex ? data : article);

        setValue(attachsPath, attachs, {shouldDirty: true});
        setValue(articlesPath, nextArticles, {shouldDirty: true});
        closeArticleModal();
    };

    const removeArticle = (individualIndex, articleIndex) => {
        const articlesPath = `individualList.${individualIndex}.articles`;
        const attachsPath = `individualList.${individualIndex}.attachs`;
        const articles = watch(articlesPath) ?? [];
        const attachs = watch(attachsPath) ?? [];
        const article = articles[articleIndex];
        const individualItemId = watch(`individualList.${individualIndex}.itemId`) ?? individualIndex;
        nextArticleItemIdRef.current[individualItemId] = Math.max(
            nextArticleItemIdRef.current[individualItemId] ?? 1,
            Number(getNextArticleItemId(articles)),
        );
        const nextAttachs = removeExclusiveTemporaryAttach(individualIndex, articleIndex, article?.attachId, attachs);

        setValue(articlesPath, articles.filter((_, index) => index !== articleIndex), {shouldDirty: true});
        setValue(attachsPath, nextAttachs, {shouldDirty: true});
    };

    const closeIndividualRegionModal = () => setIndividualRegionModal({
        open: false,
        individualIndex: null,
        regionIndex: null,
        allowNullLocal: false,
        data: emptyIndividualRegion("1"),
    });

    const openNewIndividualRegion = (individualIndex) => {
        const items = watch(`individualList.${individualIndex}.individualRegionWrapper`) ?? [];
        const individualItemId = watch(`individualList.${individualIndex}.itemId`) ?? individualIndex;
        const calculatedItemId = Number(getNextIndividualRegionItemId(items));
        const itemId = String(Math.max(calculatedItemId, nextIndividualRegionItemIdRef.current[individualItemId] ?? 1));
        nextIndividualRegionItemIdRef.current[individualItemId] = Number(itemId) + 1;
        setIndividualRegionModal({
            open: true,
            individualIndex,
            regionIndex: null,
            allowNullLocal: false,
            data: emptyIndividualRegion(itemId),
        });
    };

    const openEditIndividualRegion = (individualIndex, regionIndex, data) => setIndividualRegionModal({
        open: true,
        individualIndex,
        regionIndex,
        allowNullLocal: false,
        data: {...data},
    });

    const removeIndividualRegion = (individualIndex, regionIndex) => {
        const path = `individualList.${individualIndex}.individualRegionWrapper`;
        const items = watch(path) ?? [];
        const individualItemId = watch(`individualList.${individualIndex}.itemId`) ?? individualIndex;
        nextIndividualRegionItemIdRef.current[individualItemId] = Math.max(
            nextIndividualRegionItemIdRef.current[individualItemId] ?? 1,
            Number(getNextIndividualRegionItemId(items)),
        );
        setValue(path, items.filter((_, index) => index !== regionIndex), {shouldDirty: true});
    };

    const saveIndividualRegion = () => {
        const {individualIndex, regionIndex, allowNullLocal, data} = individualRegionModal;
        if (individualIndex === null) return;

        if (!allowNullLocal && !data.local) {
            projectUtils.swalErrorMessage("", 'O campo "Local" é obrigatório.', "Ok");
            return;
        }

        if (!data.type) {
            projectUtils.swalErrorMessage("", 'O campo "Tipo" é obrigatório.', "Ok");
            return;
        }

        if (!data.description?.trim()) {
            projectUtils.swalErrorMessage("", 'O campo "Descrição" é obrigatório.', "Ok");
            return;
        }

        const path = `individualList.${individualIndex}.individualRegionWrapper`;
        const items = watch(path) ?? [];
        const nextItems = regionIndex !== null
            ? items.map((item, index) => index === regionIndex ? {...data, description: data.description.trim()} : item)
            : [...items, {...data, description: data.description.trim()}];

        setValue(path, nextItems, {shouldDirty: true});
        closeIndividualRegionModal();
    };

    const confirmAddressLocation = (address) => {
        if (addressMap.index === null) return;

        setValue(`individualList.${addressMap.index}.address.lat`, normalizeCoordinate(address?.latitude), {shouldDirty: true});
        setValue(`individualList.${addressMap.index}.address.lng`, normalizeCoordinate(address?.longitude), {shouldDirty: true});
        setAddressMap({open: false, index: null});
    };

    const {data: individualColors} = useQuery({
        queryKey: ["individual-colors"],
        queryFn: () => individualService.individualColors(),
    });

    const {data: educationDegrees} = useQuery({
        queryKey: ["individual-instructions"],
        queryFn: () => individualService.individualInstructions(),
    });

    const {data: bodyShapes} = useQuery({
        queryKey: ["bodyShape"],
        queryFn: () => individualService.bodyShape(),
    });

    const {data: eyeColors} = useQuery({
        queryKey: ["colorEyes"],
        queryFn: () => individualService.colorEyes(),
    });

    const {data: hairTypes} = useQuery({
        queryKey: ["hairType"],
        queryFn: () => individualService.hairType(),
    });

    const {data: hairColors} = useQuery({
        queryKey: ["hairColor"],
        queryFn: () => individualService.hairColor(),
    });

    const {data: hairSizes} = useQuery({
        queryKey: ["hairSize"],
        queryFn: () => individualService.hairSize(),
    });

    const {data: situations} = useQuery({
        queryKey: ["situations"],
        queryFn: () => individualService.situations(),
    });

    const {data: individualRegions} = useQuery({
        queryKey: ["individualRegion"],
        queryFn: () => individualService.region(),
    });

    const {data: individualRegionDetails} = useQuery({
        queryKey: ["individualRegionDetail"],
        queryFn: () => individualService.regionDetail(),
    });

    const {data: systemNatures} = useQuery({
        queryKey: ["system_natures"],
        queryFn: () => natureService.getSystemNatures(),
        initialData: [],
    });

    const {data: phoneProviders} = useQuery({
        queryKey: ["phone-providers"],
        queryFn: () => individualService.phoneProviders(),
        initialData: [],
    });

    const conditionOptions = useMemo(
        () => (conditions ?? []).map((item) => ({label: item, value: item})),
        [conditions]
    );

    const referralOptions = useMemo(
        () => (referrals ?? []).map((item) => ({label: item, value: item})),
        [referrals]
    );

    const canSearchCortex = hasPermission(PermissionEnum.CORTEX_INDIVIDUAL) && loaderData?.hasCortex;

    const articleAttachs = articleModal.individualIndex === null
        ? []
        : (watch(`individualList.${articleModal.individualIndex}.attachs`) ?? []);
    const persistedArticleAttach = articleAttachs.find((attach) => attach.attachId === articleModal.data.attachId);
    const articleModalFile = articleModal.pendingAttach ?? (persistedArticleAttach && !persistedArticleAttach.file
        ? {...persistedArticleAttach, url: `${verifySyncUrl(persistedArticleAttach.attachId)}/${persistedArticleAttach.attachId}`}
        : persistedArticleAttach);

    const setArticleFile = (file) => {
        const attachId = String(Date.now());
        setArticleModal((current) => ({
            ...current,
            data: {...current.data, attachId},
            pendingAttach: {...file, attachId, description: "Anexo do artigo"},
        }));
    };

    const removeArticleFile = () => setArticleModal((current) => ({
        ...current,
        data: {...current.data, attachId: null},
        pendingAttach: null,
    }));

    const addFile = (index, target, file) => {
        const attachId = Date.now();
        const newFile = Object.assign(file, {
            attachId,
            description: target === "relatedPhotoFaceId" ? "Face do indivíduo" : "Foto do documento"
        });

        const attachs = watch(`individualList.${index}.attachs`) ?? [];

        setValue(`individualList.${index}.attachs`, [...attachs, newFile]);
        setValue(`individualList.${index}.${target}`, attachId);
        setValue(`individualList.${index}.${target}Temp`, attachId);
    };

    const removeFile = (target, index) => {
        const attachs = watch(`individualList.${index}.attachs`) ?? [];
        const attachId = watch(`individualList.${index}.${target}`);

        setValue(`individualList.${index}.attachs`, attachs.filter((attach) => attach?.attachId !== attachId));
        setValue(`individualList.${index}.${target}`, null);
        setValue(`individualList.${index}.${target}Temp`, null);
    };

    const setGeneralAttaches = (index, updater) => {
        const path = `individualList.${index}.attachs`;
        const current = watch(path) ?? [];
        const updated = typeof updater === "function" ? updater(current) : updater;
        const normalized = updated.map((attach) => {
            if (!attach?.new || attach?.file) return attach;

            return {
                attachId: attach.attachId,
                name: attach.name,
                type: attach.type,
                extension: attach.type,
                description: attach.description ?? null,
                url: attach.url,
                file: attach,
            };
        });

        setValue(path, normalized, {shouldDirty: true});
    };

    const setGeneralAttachDescription = (index, attachId, description) => {
        const path = `individualList.${index}.attachs`;
        const attachs = watch(path) ?? [];
        setValue(path, attachs.map((attach) => attach.attachId === attachId
            ? {...attach, description}
            : attach), {shouldDirty: true});
    };

    const isSpecialAttach = (individual, attachId) => individual.relatedPhotoFaceId === attachId
        || individual.documentPhotoId === attachId
        || individual.individualVersion?.some((version) => version.signatureId === attachId)
        || individual.articles?.some((article) => article.attachId === attachId);

    const populateIndividualData = (index, data) => {
        const address = data?.addresses?.find((item) => item.type !== "COMERCIAL") ?? data?.address ?? {};
        const companyAddress = data?.addresses?.find((item) => item.type === "COMERCIAL") ?? {};
        const phones = Array.isArray(data?.phones) ? data.phones : [];
        const personalPhone = phones.find((item) => item?.type === "RESIDENCIAL" && item?.data)
            ?? phones.find((item) => item?.data);
        const phone = personalPhone?.data ?? data?.phone ?? "";
        const phoneProvider = personalPhone?.phoneProvider ?? data?.phoneProvider ?? null;
        const sex = data?.sex ? (data.sex === "MASCULINO" ? "MALE" : data.sex === "FEMININO" ? "FEMALE" : data.sex) : "";

        const payload = {
            name: data?.name ?? "",
            dtBirth: projectUtils.toDate(data?.dtBirth, "dd/MM/yyyy"),
            father: data?.father ?? "",
            mother: data?.mother ?? "",
            emitterRg: data?.emitterRg ?? "",
            rg: data?.rg ?? "",
            criminalRg: data?.criminalRg ?? "",
            cnh: data?.cnh ?? "",
            nickname: data?.nickname ?? "",
            socialName: data?.socialName ?? "",
            phone,
            phoneProvider,
            occupation: data?.occupation ?? "",
            height: data?.height ?? "",
            bodyShape: data?.bodyShape ?? "",
            eyes: data?.eyes ?? "",
            hairType: data?.hairType ?? "",
            hairColor: data?.hairColor ?? "",
            hairSize: data?.hairSize ?? "",
            roleCrime: data?.roleCrime ?? "",
            situation: data?.situation ?? "",
            socialNetwork: data?.socialNetwork ?? "",
            information: data?.information ?? "",
            individualRegionWrapper: normalizeImportedIndividualRegions(data),
            sex,
            nationality: data?.nationality,
            maritalStatus: mapLegacyMaritalStatus(data?.maritalStatus),
            color: data?.color ?? "",
            educationDegree: data?.educationDegree ?? "",
            mentalDisorder: !!data?.mentalDisorder,
            alcoholDrugDependency: !!data?.alcoholDrugDependency,
            address: {
                address: address?.address ?? "",
                number: address?.number ?? "",
                complement: address?.complement ?? "",
                zipCode: address?.zipCode ?? "",
                neighborhood: address?.neighborhood ?? "",
                city: address?.city ?? "",
                date: address?.date ?? null,
                lat: normalizeCoordinate(address?.lat),
                lng: normalizeCoordinate(address?.lng),
            },
            cityOfBirth: data?.nationality === "BRASIL" ? (data?.cityOfBirth ?? "") : null,
            homeless: !!data?.homeless,
            companyAddress: {
                address: companyAddress?.address ?? "",
                neighborhood: companyAddress?.neighborhood ?? "",
                city: companyAddress?.city ?? "",
                number: companyAddress?.number ?? "",
                complement: companyAddress?.complement ?? "",
                zipCode: companyAddress?.zipCode ?? "",
            },
        };

        Object.entries(payload).forEach(([key, value]) => {
            setValue(`individualList.${index}.${key}`, value);
        });
    };

    const loadIndividualByCpf = async (index) => {
        const itemId = watch(`individualList.${index}.itemId`);
        const cpf = (watch(`individualList.${index}.cpf`) ?? "").replace(/\D/g, "");

        if (!cpf || loadedCpfRef.current[itemId] === cpf) return;

        try {
            const response = await individualService.byCpf(cpf);
            const rawData = response?.data?.data?.data;

            if (!rawData) return;

            loadedCpfRef.current[itemId] = cpf;
            populateIndividualData(index, JSON.parse(rawData));
        } catch (error) {
            console.error(error);
        }
    };

    const searchCortex = async (index) => {
        if (!canSearchCortex) return;

        const itemId = watch(`individualList.${index}.itemId`);
        const cpf = (watch(`individualList.${index}.cpf`) ?? "").replace(/\D/g, "");
        if (!cpf) {
            projectUtils.swalMessage("warning", "", "Informe o CPF para realizar a pesquisa.", "Ok");
            return;
        }

        setIsLoading(true);
        try {
            const {data} = await cortexService.personDataByCpf(cpf);

            loadedCpfRef.current[itemId] = cpf;
            populateIndividualData(index, data);
        } catch (error) {
            console.error(error);

            let message = "Ocorreu um erro na requisição de dados do indivíduo.";
            const code = error?.response?.data?.message;

            switch (code) {
                case "cortex.person.cpf": message = "O campo \"CPF\"  é obrigatório."; break;
                case "cortex.not.enabled": message = "Cortex desativado."; break;
                case "cortex.cpf.invalid": message = "O usuário atual não possui CPF cadastrado."; break;
                case "cortex.token.expired": message = "Chave do cortex expirada."; break;
                case "cortex.token.invalid": message = "Chave do cortex inválida."; break;
                case "cortex.not.allow.renavam": message = "Usuário sem permissão de acesso ao CÓRTEX - RENAVAM."; break;
                case "cortex.not.allow.passages": message = "Usuário sem permissão de acesso ao CÓRTEX - PASSAGENS."; break;
                case "cortex.not.allow.individual": message = "Usuário sem permissão de acesso ao CÓRTEX - INDIVÍDUOS."; break;
            }

            projectUtils.swalErrorMessage("", message, "Ok");
        } finally {
            setIsLoading(false);
        }
    };

    const updateNewVersion = (index, changes) => {
        const versions = watch(`individualList.${index}.individualVersion`) ?? [];
        const lastVersion = getCurrentVersion(versions);
        const version = {
            itemId: lastVersion?.itemId ?? 1,
            signOption: null,
            nothingToDeclare: lastVersion?.nothingToDeclare ?? false,
            version: lastVersion?.version ?? "",
            ...changes,
        };

        setValue(
            `individualList.${index}.individualVersion`,
            versions.length ? [...versions.slice(0, -1), version] : [version],
            {shouldDirty: true}
        );
    };

    const changeVersionMode = (index, mode, currentVersion) => {
        const nothingToDeclare = mode === "nothing-to-declare";
        const versionTextKey = `individual-${watch(`individualList.${index}.itemId`) ?? index}`;

        if (nothingToDeclare && !currentVersion?.nothingToDeclare) {
            versionTextRef.current[versionTextKey] = currentVersion?.version ?? "";
        }

        updateNewVersion(index, {
            nothingToDeclare,
            version: nothingToDeclare
                ? ""
                : (versionTextRef.current[versionTextKey] ?? currentVersion?.version ?? ""),
        });
    };

    const startPersistedVersionEditing = (index) => {
        const versions = watch(`individualList.${index}.individualVersion`) ?? [];
        const lastVersion = getCurrentVersion(versions);
        const newVersion = {
            itemId: Number(lastVersion?.itemId ?? 0) + 1,
            signOption: null,
            nothingToDeclare: lastVersion?.nothingToDeclare ?? false,
            version: lastVersion?.version ?? "",
        };

        const versionTextKey = `individual-${watch(`individualList.${index}.itemId`) ?? index}`;
        versionTextRef.current[versionTextKey] = newVersion.version;
        setValue(`individualList.${index}.individualVersion`, [...versions, newVersion], {shouldDirty: true});
        setEditingPersistedVersionIndexes((current) => current.includes(index) ? current : [...current, index]);
    };

    const editPersistedVersion = (index) => {
        const versions = watch(`individualList.${index}.individualVersion`) ?? [];
        const currentVersion = getCurrentVersion(versions);

        if (currentVersion?.signOption === "SIGNATURE") {
            projectUtils.swalMessage(
                "warning",
                "Atenção",
                'A alteração na "Versão do indivíduo" irá remover a assinatura do indivíduo. Deseja continuar?',
                "Sim",
                true,
                "Não",
                (result) => {
                    if (result?.isConfirmed) startPersistedVersionEditing(index);
                }
            );
            return;
        }

        startPersistedVersionEditing(index);
    };

    return (
        <>
            <Card>
                <Card.Header>
                    <Card.Title>Indivíduos</Card.Title>
                </Card.Header>

                <Card.Body className={"space-y-4"}>
                    {
                        fields.map((item, index) => {
                            const versions = watch(`individualList.${index}.individualVersion`);
                            const currentVersion = getCurrentVersion(versions);
                            const individualRegionWrapper = watch(`individualList.${index}.individualRegionWrapper`) ?? [];
                            const articles = watch(`individualList.${index}.articles`) ?? [];
                            const individual = watch(`individualList.${index}`) ?? {};
                            const generalAttaches = (individual.attachs ?? []).filter((attach) =>
                                !isSpecialAttach(individual, attach.attachId));
                            const allowRemove = isSync || !!item.__isNew;
                            const canEditPersistedVersion = isSync && !item.__isNew;
                            const isVersionEditable = !!item.__isNew || editingPersistedVersionIndexes.includes(index);
                            const versionMode = currentVersion?.nothingToDeclare ? "nothing-to-declare" : "write";
                            const canReviseVersion = isVersionEditable && !currentVersion?.nothingToDeclare && loaderData?.canUseLlm;
                            const versionTextKey = `individual-${individual.itemId ?? index}`;
                            const cityOfBirthDisabled = watch(`individualList.${index}.nationality`) !== "BRASIL";

                            return (
                                <SentryRow className={"p-4 border border-[#e5e7eb] rounded-md relative"} key={"individual-" + item.itemId}>
                                    {
                                        allowRemove &&
                                        <Button className={"absolute -top-[10px] -right-[10px] !py-0 !px-0 !w-[25px] !h-[25px] z-10"} target={"danger"} onClick={() => remove(index)}>
                                            <FaTrash />
                                        </Button>
                                    }

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.itemId`}
                                        label={""}
                                        classNameWrapper={"hidden"}
                                    />

                                    <FormField
                                        name={`individualList.${index}.relatedPhotoFaceId`}
                                        customRender={field => {
                                            const attachs = watch(`individualList.${index}.attachs`) ?? [];
                                            const isRemove = !!watch(`individualList.${index}.relatedPhotoFaceIdTemp`);
                                            const file = attachs.find((attach) => attach?.attachId === field.value);
                                            if (file && !file?.file) {
                                                file.url = `${verifySyncUrl(file.attachId)}/${file.attachId}`;
                                            }

                                            return (
                                                <InputFile
                                                    file={file}
                                                    setFile={(file) => addFile(index, "relatedPhotoFaceId", file)}
                                                    removeFile={() => removeFile("relatedPhotoFaceId", index)}
                                                    title={"Foto da face"}
                                                    titleWithFile={"Foto da face"}
                                                    isRemove={!!isRemove}
                                                    className={"col-span-2"}
                                                />
                                            );
                                        }}
                                    />

                                    <FormField
                                        name={`individualList.${index}.documentPhotoId`}
                                        customRender={field => {
                                            const attachs = watch(`individualList.${index}.attachs`) ?? [];
                                            const isRemove = !!watch(`individualList.${index}.documentPhotoIdTemp`);
                                            const file = attachs.find((attach) => attach?.attachId === field.value);
                                            if (file && !file?.file) {
                                                file.url = `${verifySyncUrl(file.attachId)}/${file.attachId}`;
                                            }

                                            return (
                                                <InputFile
                                                    file={file}
                                                    setFile={(file) => addFile(index, "documentPhotoId", file)}
                                                    removeFile={() => removeFile("documentPhotoId", index)}
                                                    title={"Documento"}
                                                    titleWithFile={"Documento"}
                                                    isRemove={!!isRemove}
                                                    className={"col-span-2"}
                                                />
                                            );
                                        }}
                                    />

                                    <FormField
                                        name={`individualList.${index}.cpf`}
                                        customRender={(field) => (
                                            <div className={"col-span-2 flex gap-2"}>
                                                <InputForm
                                                    id={`individualList.${index}.cpf`}
                                                    name={field.name}
                                                    label={"CPF *"}
                                                    classNameWrapper={"w-full"}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    onBlur={() => loadIndividualByCpf(index)}
                                                    mask={MaskDefault.cpf}
                                                />
                                                <Button
                                                    target={"primary-outline"}
                                                    title={"Pesquisar no Córtex"}
                                                    onClick={() => searchCortex(index)}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    disabled={!canSearchCortex}
                                                >
                                                    <img
                                                        className={"w-[20px] h-[20px]"}
                                                        src={getAssetPath(canSearchCortex ? "img/cortex-enabled.svg" : "img/cortex-disabled.svg")}
                                                        alt=""
                                                    />
                                                </Button>
                                            </div>
                                        )}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.name`}
                                        label={"Nome *"}
                                        classNameWrapper={"col-span-3"}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.socialName`}
                                        label={"Nome social"}
                                        classNameWrapper={"col-span-3"}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.nickname`}
                                        label={"Apelido"}
                                        classNameWrapper={"col-span-2"}
                                    />

                                    <FormField
                                        name={`individualList.${index}.sex`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.sex`}
                                                value={sexOptions.find((option) => option.value === field.value) ?? null}
                                                options={sexOptions}
                                                label={"Sexo"}
                                                classNameWrapper={"col-span-2"}
                                                onChange={(selected) => field.onChange(selected?.value)}
                                            />
                                        )}
                                    />

                                    <FormField
                                        name={`individualList.${index}.dtBirth`}
                                        customRender={(field) => (
                                            <InputDateTime
                                                id={`individualList.${index}.dtBirth`}
                                                selected={projectUtils.toDate(field?.value, "dd/MM/yyyy")}
                                                label={"Data de nascimento"}
                                                onChange={field.onChange}
                                                classNameWrapper={"col-span-2"}
                                                dateFormat={"L"}
                                            />
                                        )}
                                    />

                                    <FormField
                                        name={`individualList.${index}.conditions`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.conditions`}
                                                value={buildMultiValue(field.value, conditionOptions)}
                                                options={conditionOptions}
                                                label={"Condições *"}
                                                classNameWrapper={"col-span-3"}
                                                isMulti
                                                onChange={(selected) => field.onChange(selected?.map((item) => item.value) ?? [])}
                                            />
                                        )}
                                    />

                                    <FormField
                                        name={`individualList.${index}.referral`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.referral`}
                                                value={buildMultiValue(field.value, referralOptions)}
                                                options={referralOptions}
                                                label={"Encaminhamentos *"}
                                                classNameWrapper={"col-span-3"}
                                                isMulti
                                                onChange={(selected) => field.onChange(selected?.map((item) => item.value) ?? [])}
                                            />
                                        )}
                                    />

                                    <div className={"col-span-full border-t border-[#e5e7eb] pt-2"}>
                                        <h4 className={"text-sm font-semibold text-gray-500"}>Documentos</h4>
                                    </div>

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.rg`}
                                        label={"RG"}
                                        classNameWrapper={"col-span-3"}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.emitterRg`}
                                        label={"Emissor"}
                                        classNameWrapper={"col-span-2"}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.criminalRg`}
                                        label={"RG criminal"}
                                        classNameWrapper={"col-span-3"}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.cnh`}
                                        label={"CNH"}
                                        classNameWrapper={"col-span-2"}
                                    />

                                    <FormField
                                        name={`individualList.${index}.cnhCategories`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.cnhCategories`}
                                                value={buildMultiValue(field.value, cnhCategoriesOptions)}
                                                options={cnhCategoriesOptions}
                                                label={"Categoria CNH"}
                                                classNameWrapper={"col-span-2"}
                                                isMulti
                                                onChange={(selected) => field.onChange(selected?.map((item) => item.value) ?? [])}
                                            />
                                        )}
                                    />

                                    <FormField
                                        name={`individualList.${index}.medicalExamDueDate`}
                                        customRender={(field) => (
                                            <InputDateTime
                                                id={`individualList.${index}.medicalExamDueDate`}
                                                selected={projectUtils.toDate(field?.value, "dd/MM/yyyy")}
                                                label={"Vencimento do exame médico"}
                                                onChange={field.onChange}
                                                classNameWrapper={"col-span-3"}
                                                dateFormat={"L"}
                                            />
                                        )}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.otherDocument`}
                                        label={"Outro documento"}
                                        classNameWrapper={"col-span-3"}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.otherDocumentValue`}
                                        label={"Número"}
                                        classNameWrapper={"col-span-3"}
                                    />

                                    <div className={"col-span-full border-t border-[#e5e7eb] pt-2"}>
                                        <h4 className={"text-sm font-semibold text-gray-500"}>Informações adicionais</h4>
                                    </div>

                                    <FormField
                                        name={`individualList.${index}.maritalStatus`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.maritalStatus`}
                                                value={maritalStatusOptions.find((option) => option.value === field.value) ?? null}
                                                options={maritalStatusOptions}
                                                label={"Estado civil"}
                                                classNameWrapper={"col-span-2"}
                                                onChange={(selected) => field.onChange(selected?.value)}
                                            />
                                        )}
                                    />

                                    <FormField
                                        name={`individualList.${index}.nationality`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.nationality`}
                                                classNameWrapper={"col-span-3"}
                                                loadOptions={getCountries}
                                                value={optionsUtils.getValue(field.value)}
                                                isAsync={true}
                                                label={"Nacionalidade"}
                                                onChange={(selected) => {
                                                    const value = selected?.value ?? null;
                                                    field.onChange(value);

                                                    if (value !== "BRASIL") {
                                                        setValue(`individualList.${index}.cityOfBirth`, null);
                                                    }
                                                }}
                                                cacheOptions
                                                defaultOptions={[]}
                                            />
                                        )}
                                    />

                                    <FormField
                                        name={`individualList.${index}.cityOfBirth`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.cityOfBirth`}
                                                classNameWrapper={"col-span-3"}
                                                loadOptions={getCities}
                                                value={optionsUtils.getValue(field.value)}
                                                isAsync={true}
                                                label={"Naturalidade"}
                                                onChange={(selected) => field.onChange(selected?.value)}
                                                isDisabled={cityOfBirthDisabled}
                                                cacheOptions
                                                defaultOptions={[]}
                                            />
                                        )}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.email`}
                                        label={"E-mail"}
                                        classNameWrapper={"col-span-4"}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.phone`}
                                        label={"Telefone"}
                                        classNameWrapper={"col-span-2"}
                                        mask={MaskDefault.phone}
                                    />

                                    <FormField
                                        name={`individualList.${index}.phoneProvider`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.phoneProvider`}
                                                label={"Operadora (Telefone)"}
                                                classNameWrapper={"col-span-2"}
                                                options={phoneProviders}
                                                value={phoneProviders.find((option) => option.value === field.value)
                                                    ?? optionsUtils.getValue(field.value)}
                                                onChange={(selected) => field.onChange(selected?.value ?? null)}
                                            />
                                        )}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.mother`}
                                        label={"Nome da mãe"}
                                        classNameWrapper={"col-span-4"}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.father`}
                                        label={"Nome do pai"}
                                        classNameWrapper={"col-span-4"}
                                    />

                                    <div className={"col-span-full flex gap-4"}>
                                        <FormField
                                            name={`individualList.${index}.mentalDisorder`}
                                            customRender={field => (
                                                <InputSwitch
                                                    className={"w-[200px]"}
                                                    id={`individualList.${index}.mentalDisorder`}
                                                    label={"Possui transtorno mental"}
                                                    checked={!!field.value}
                                                    onChange={field.onChange}
                                                />
                                            )}
                                        />
                                        <FormField
                                            name={`individualList.${index}.alcoholDrugDependency`}
                                            customRender={field => (
                                                <InputSwitch
                                                    className={"w-[210px]"}
                                                    id={`individualList.${index}.alcoholDrugDependency`}
                                                    label={"Dependência álcool/drogas"}
                                                    checked={!!field.value}
                                                    onChange={field.onChange}
                                                />
                                            )}
                                        />
                                    </div>

                                    <div className={"col-span-full border-t border-[#e5e7eb] pt-2"}>
                                        <h4 className={"text-sm font-semibold text-gray-500"}>Características</h4>
                                    </div>

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.height`}
                                        label={"Altura (cm)"}
                                        type={"number"}
                                        integerOnly
                                        classNameWrapper={"col-span-2"}
                                    />

                                    <FormField
                                        name={`individualList.${index}.color`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.color`}
                                                value={individualColors?.find((option) => option.value === field.value) ?? optionsUtils.getValue(field.value)}
                                                options={individualColors ?? []}
                                                label={"Cútis"}
                                                classNameWrapper={"col-span-2"}
                                                onChange={(selected) => field.onChange(selected?.value)}
                                            />
                                        )}
                                    />

                                    <FormField
                                        name={`individualList.${index}.bodyShape`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.bodyShape`}
                                                value={bodyShapes?.find((option) => option.value === field.value) ?? optionsUtils.getValue(field.value)}
                                                options={bodyShapes ?? []}
                                                label={"Compleição"}
                                                classNameWrapper={"col-span-2"}
                                                onChange={(selected) => field.onChange(selected?.value)}
                                            />
                                        )}
                                    />

                                    <FormField
                                        name={`individualList.${index}.eyes`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.eyes`}
                                                value={eyeColors?.find((option) => option.value === field.value) ?? optionsUtils.getValue(field.value)}
                                                options={eyeColors ?? []}
                                                label={"Cor dos olhos"}
                                                classNameWrapper={"col-span-2"}
                                                onChange={(selected) => field.onChange(selected?.value)}
                                            />
                                        )}
                                    />

                                    <FormField
                                        name={`individualList.${index}.hairType`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.hairType`}
                                                value={hairTypes?.find((option) => option.value === field.value) ?? optionsUtils.getValue(field.value)}
                                                options={hairTypes ?? []}
                                                label={"Tipo dos cabelos"}
                                                classNameWrapper={"col-span-4"}
                                                onChange={(selected) => field.onChange(selected?.value)}
                                            />
                                        )}
                                    />

                                    <FormField
                                        name={`individualList.${index}.hairColor`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.hairColor`}
                                                value={hairColors?.find((option) => option.value === field.value) ?? optionsUtils.getValue(field.value)}
                                                options={hairColors ?? []}
                                                label={"Cor dos cabelos"}
                                                classNameWrapper={"col-span-3"}
                                                onChange={(selected) => field.onChange(selected?.value)}
                                            />
                                        )}
                                    />

                                    <FormField
                                        name={`individualList.${index}.hairSize`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.hairSize`}
                                                value={hairSizes?.find((option) => option.value === field.value) ?? optionsUtils.getValue(field.value)}
                                                options={hairSizes ?? []}
                                                label={"Comprimento dos cabelos"}
                                                classNameWrapper={"col-span-3"}
                                                onChange={(selected) => field.onChange(selected?.value)}
                                            />
                                        )}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.injury`}
                                        label={"Ferimento/Lesão"}
                                        classNameWrapper={"col-span-6"}
                                    />

                                    <div className={"col-span-full border-t border-[#e5e7eb] pt-2 flex items-center gap-2"}>
                                        <h4 className={"text-sm font-semibold text-gray-500 translate-y-1"}>Outras características</h4>
                                        <Button type={"button"} className={"h-7.5! w-7.5 p-0! translate-y-1"}
                                                title={"Adicionar característica"}
                                                onClick={() => openNewIndividualRegion(index)}>
                                            <FaPlus/>
                                        </Button>
                                    </div>

                                    <div className={"col-span-full space-y-2"}>
                                        {individualRegionWrapper.map((region, regionIndex) => (
                                            <div key={region.itemId ?? regionIndex}
                                                 className={"grid grid-cols-12 gap-2 items-center border border-[#e5e7eb] rounded-md p-2 text-sm"}>
                                                <span className={"col-span-3"}><strong>Local:</strong> {region.local ?? "-"}</span>
                                                <span className={"col-span-3"}><strong>Tipo:</strong> {region.type ?? "-"}</span>
                                                <span className={"col-span-4"}><strong>Descrição:</strong> {region.description ?? "-"}</span>
                                                <div className={"col-span-2 flex justify-end gap-1"}>
                                                    <Button type={"button"} className={"h-7.5! w-7.5 p-0!"}
                                                            title={"Editar característica"}
                                                            onClick={() => openEditIndividualRegion(index, regionIndex, region)}>
                                                        <FaPen/>
                                                    </Button>
                                                    <Button type={"button"} target={"danger"} className={"h-7.5! w-7.5 p-0!"}
                                                            title={"Excluir característica"}
                                                            onClick={() => removeIndividualRegion(index, regionIndex)}>
                                                        <FaTrash/>
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className={"col-span-full border-t border-[#e5e7eb] pt-2"}>
                                        <h4 className={"text-sm font-semibold text-gray-500"}>Endereço</h4>
                                    </div>

                                    <FormField
                                        name={`individualList.${index}.homeless`}
                                        customRender={(field) => (
                                            <InputCheckbox
                                                id={`individualList.${index}.homeless`}
                                                checked={!!field.value}
                                                label={"Situação de rua"}
                                                onChange={({target: {checked}}) => field.onChange(checked)}
                                                className={"col-span-12"}
                                            />
                                        )}
                                    />

                                    <FormField
                                        name={`individualList.${index}.address.address`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.address.address`}
                                                classNameWrapper={"col-span-6"}
                                                loadOptions={getStreets}
                                                value={optionsUtils.getValue(field.value)}
                                                isAsync={true}
                                                label={"Logradouro"}
                                                onChange={(selected) => field.onChange(selected?.value)}
                                                cacheOptions
                                                defaultOptions={[]}
                                            />
                                        )}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.address.number`}
                                        label={"Número"}
                                        classNameWrapper={"col-span-1"}
                                    />

                                    <FormField
                                        name={`individualList.${index}.address.neighborhood`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.address.neighborhood`}
                                                classNameWrapper={"col-span-5"}
                                                loadOptions={getNeighborhoods}
                                                value={optionsUtils.getValue(field.value)}
                                                isAsync={true}
                                                label={"Bairro"}
                                                onChange={(selected) => field.onChange(selected?.value)}
                                                cacheOptions
                                                defaultOptions={[]}
                                            />
                                        )}
                                    />

                                    <FormField
                                        name={`individualList.${index}.address.city`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.address.city`}
                                                classNameWrapper={"col-span-4"}
                                                loadOptions={getCities}
                                                value={optionsUtils.getValue(field.value)}
                                                isAsync={true}
                                                label={"Município"}
                                                onChange={(selected) => field.onChange(selected?.value)}
                                                cacheOptions
                                                defaultOptions={[]}
                                            />
                                        )}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.address.complement`}
                                        label={"Complemento"}
                                        classNameWrapper={"col-span-6"}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.address.zipCode`}
                                        label={"CEP"}
                                        classNameWrapper={"col-span-2"}
                                        mask={MaskDefault.cep}
                                    />

                                    <FormField
                                        name={`individualList.${index}.address.date`}
                                        customRender={(field) => (
                                            <InputDateTime
                                                id={`individualList.${index}.address.date`}
                                                label={"Data"}
                                                selected={field.value}
                                                onChange={(date) => field.onChange(date ? format(date, "yyyy-MM-dd") : null)}
                                                classNameWrapper={"col-span-2"}
                                                dateFormat={"L"}
                                            />
                                        )}
                                    />

                                    <FormField
                                        name={`individualList.${index}.address.lat`}
                                        customRender={(field) => (
                                            <InputForm
                                                id={`individualList.${index}.address.lat`}
                                                label={"Latitude"}
                                                type={"number"}
                                                value={field.value ?? ""}
                                                onChange={({target}) => field.onChange(target.value === "" ? null : target.value)}
                                                onBlur={({target}) => field.onChange(normalizeCoordinate(target.value))}
                                                classNameWrapper={"col-span-4"}
                                            />
                                        )}
                                    />

                                    <FormField
                                        name={`individualList.${index}.address.lng`}
                                        customRender={(field) => (
                                            <InputForm
                                                id={`individualList.${index}.address.lng`}
                                                label={"Longitude"}
                                                type={"number"}
                                                value={field.value ?? ""}
                                                onChange={({target}) => field.onChange(target.value === "" ? null : target.value)}
                                                onBlur={({target}) => field.onChange(normalizeCoordinate(target.value))}
                                                classNameWrapper={"col-span-4"}
                                            />
                                        )}
                                    />

                                    <Button
                                        type={"button"}
                                        className={"col-span-2 h-[32px] w-full"}
                                        title={"Selecionar localização no mapa"}
                                        onClick={() => setAddressMap({open: true, index})}
                                    >
                                        Mapa
                                    </Button>

                                    <div className={"col-span-full border-t border-[#e5e7eb] pt-2"}>
                                        <h4 className={"text-sm font-semibold text-gray-500"}>Comercial</h4>
                                    </div>

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.companyName`}
                                        label={"Empresa"}
                                        classNameWrapper={"col-span-4"}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.companyPhone`}
                                        label={"Telefone"}
                                        classNameWrapper={"col-span-4"}
                                        mask={MaskDefault.phone}
                                    />

                                    <FormField
                                        name={`individualList.${index}.companyAddress.address`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.companyAddress.address`}
                                                classNameWrapper={"col-span-4"}
                                                loadOptions={getStreets}
                                                value={optionsUtils.getValue(field.value)}
                                                isAsync={true}
                                                label={"Logradouro"}
                                                onChange={(selected) => field.onChange(selected?.value)}
                                                cacheOptions
                                                defaultOptions={[]}
                                            />
                                        )}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.companyAddress.number`}
                                        label={"Número"}
                                        classNameWrapper={"col-span-4"}
                                    />

                                    <FormField
                                        name={`individualList.${index}.companyAddress.neighborhood`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.companyAddress.neighborhood`}
                                                classNameWrapper={"col-span-4"}
                                                loadOptions={getNeighborhoods}
                                                value={optionsUtils.getValue(field.value)}
                                                isAsync={true}
                                                label={"Bairro"}
                                                onChange={(selected) => field.onChange(selected?.value)}
                                                cacheOptions
                                                defaultOptions={[]}
                                            />
                                        )}
                                    />

                                    <FormField
                                        name={`individualList.${index}.companyAddress.city`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.companyAddress.city`}
                                                classNameWrapper={"col-span-4"}
                                                loadOptions={getCities}
                                                value={optionsUtils.getValue(field.value)}
                                                isAsync={true}
                                                label={"Município"}
                                                onChange={(selected) => field.onChange(selected?.value)}
                                                cacheOptions
                                                defaultOptions={[]}
                                            />
                                        )}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.companyAddress.complement`}
                                        label={"Complemento"}
                                        classNameWrapper={"col-span-4"}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.companyAddress.zipCode`}
                                        label={"CEP"}
                                        classNameWrapper={"col-span-4"}
                                        mask={MaskDefault.cep}
                                    />

                                    <div className={"col-span-full border-t border-[#e5e7eb] pt-2"}>
                                        <h4 className={"text-sm font-semibold text-gray-500"}>Outras informações</h4>
                                    </div>

                                    <FormField
                                        name={`individualList.${index}.educationDegree`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.educationDegree`}
                                                value={educationDegrees?.find((option) => option.value === field.value) ?? optionsUtils.getValue(field.value)}
                                                options={educationDegrees ?? []}
                                                label={"Grau de instrução"}
                                                classNameWrapper={"col-span-3"}
                                                onChange={(selected) => field.onChange(selected?.value)}
                                            />
                                        )}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.occupation`}
                                        label={"Profissão"}
                                        classNameWrapper={"col-span-2"}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.roleCrime`}
                                        label={"Função no crime"}
                                        classNameWrapper={"col-span-3"}
                                    />

                                    <FormField
                                        name={`individualList.${index}.situation`}
                                        customRender={field => (
                                            <SelectComponent
                                                instanceId={`individualList.${index}.situation`}
                                                value={situations?.find((option) => option.value === field.value) ?? optionsUtils.getValue(field.value)}
                                                options={situations ?? []}
                                                label={"Situação"}
                                                classNameWrapper={"col-span-2"}
                                                onChange={(selected) => field.onChange(selected?.value)}
                                            />
                                        )}
                                    />

                                    <FormField
                                        as={InputForm}
                                        name={`individualList.${index}.socialNetwork`}
                                        label={"Rede social"}
                                        classNameWrapper={"col-span-2"}
                                    />

                                    <FormField
                                        as={InputTextarea}
                                        name={`individualList.${index}.information`}
                                        label={"Informações do indivíduo"}
                                        className={"col-span-12"}
                                        textAreaClass={"h-[80px]"}
                                    />

                                    <div className={"col-span-full border-t border-[#e5e7eb] pt-2 flex items-center gap-2"}>
                                        <h4 className={"text-sm font-semibold text-gray-500 translate-y-1"}>Artigos indiciados</h4>
                                        <Button type={"button"} className={"h-7.5! w-7.5 p-0! translate-y-1"} title={"Adicionar artigo"}
                                                onClick={() => openNewArticle(index)}>
                                            <FaPlus/>
                                        </Button>
                                    </div>

                                    <div className={"col-span-full space-y-2"}>
                                        {articles.map((article, articleIndex) => (
                                            <div key={article.itemId ?? articleIndex}
                                                 className={"grid grid-cols-12 gap-2 items-center border border-[#e5e7eb] rounded-md p-2 text-sm"}>
                                                <span className={"col-span-2"}><strong>Data:</strong> {article.date ? projectUtils.formatDate(article.date, "DD/MM/YYYY") : "-"}</span>
                                                <span className={"col-span-2"}><strong>Boletim:</strong> {article.bo || "-"}</span>
                                                <span className={"col-span-4"}><strong>Natureza:</strong> {article.article || "-"}</span>
                                                <span className={"col-span-2"}><strong>Anexo:</strong> {article.attachId ? "Sim" : "Não"}</span>
                                                <div className={"col-span-2 flex justify-end gap-1"}>
                                                    <Button type={"button"} className={"h-7.5! w-7.5 p-0!"} title={"Editar artigo"}
                                                            onClick={() => openEditArticle(index, articleIndex, article)}>
                                                        <FaPen/>
                                                    </Button>
                                                    <Button type={"button"} target={"danger"} className={"h-7.5! w-7.5 p-0!"} title={"Excluir artigo"}
                                                            onClick={() => removeArticle(index, articleIndex)}>
                                                        <FaTrash/>
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className={"col-span-full border-t border-[#e5e7eb] pt-2"}>
                                        <h4 className={"text-sm font-semibold text-gray-500"}>Versão do indivíduo *</h4>
                                    </div>

                                    <div className={"col-span-12 flex"}>
                                        <fieldset disabled={!isVersionEditable}>
                                            <ToggleButton
                                                viewMode={versionMode}
                                                setViewMode={(mode) => changeVersionMode(index, mode, currentVersion)}
                                                options={[
                                                    {key: "write", label: "Inserir"},
                                                    {key: "nothing-to-declare", label: "Nada a declarar"},
                                                ]}
                                            />
                                        </fieldset>
                                    </div>

                                    {
                                        currentVersion?.nothingToDeclare
                                            ? <div className={"col-span-12 flex flex-col items-center justify-center rounded-md bg-gray-50 py-6 text-center"}>
                                                <p className={"text-sm text-gray-500"}>Nenhuma declaração será registrada para este indivíduo</p>
                                            </div>
                                            : <>
                                                <InputTextarea
                                                    id={`individualList.${index}.individualVersionPreview`}
                                                    className={"col-span-12"}
                                                    textAreaClass={"h-28"}
                                                    value={currentVersion?.version ?? ""}
                                                    disabled={!isVersionEditable}
                                                    onChange={({target: {value}}) => {
                                                        versionTextRef.current[versionTextKey] = value;
                                                        updateNewVersion(index, {version: value});
                                                    }}
                                                />
                                            </>
                                    }

                                    {(canReviseVersion || !!currentVersion?.signOption) &&
                                        <div className={"col-span-12 flex items-center gap-4"}>
                                            {canReviseVersion &&
                                                <ReviseTextButton
                                                    value={currentVersion?.version ?? ""}
                                                    setValue={(value) => {
                                                        versionTextRef.current[versionTextKey] = value;
                                                        updateNewVersion(index, {version: value});
                                                    }}
                                                    setIsLoading={setIsLoading}
                                                />
                                            }
                                            {!!currentVersion?.signOption &&
                                                <InputCheckbox
                                                    id={`individualList.${index}.signOption`}
                                                    className={"items-center!"}
                                                    classNameLabel={"translate-y-0!"}
                                                    checked={true}
                                                    label={BoKeys[currentVersion.signOption] ?? currentVersion.signOption}
                                                    disabled
                                                    onChange={() => {}}
                                                />
                                            }
                                        </div>
                                    }

                                    {canEditPersistedVersion && !isVersionEditable &&
                                        <div className={"col-span-12"}>
                                            <Button type={"button"} onClick={() => editPersistedVersion(index)}>
                                                Editar versão do indivíduo
                                            </Button>
                                        </div>
                                    }

                                    <div className={"col-span-full border-t border-[#e5e7eb] pt-2"}>
                                        <h4 className={"text-sm font-semibold text-gray-500"}>Fotos</h4>
                                    </div>

                                    <div className={"col-span-full [&_.dropzone-preview]:h-[250px]"}>
                                        <Dropzone
                                            withoutCard
                                            attaches={generalAttaches}
                                            setAttaches={(updater) => setGeneralAttaches(index, updater)}
                                            getAttachDescription={(attach) => attach.description}
                                            onAttachDescriptionChange={(attachId, description) => setGeneralAttachDescription(index, attachId, description)}
                                            canRemove={(attach) => !isSpecialAttach(watch(`individualList.${index}`) ?? {}, attach?.attachId)}
                                            resolveAttachUrl={(attach) => `${verifySyncUrl(attach.attachId)}/${attach.attachId}`}
                                            acceptedMimeTypes={["image/*"]}
                                            invalidFileTypeMessage={"Apenas arquivos de imagem são permitidos."}
                                        />
                                    </div>
                                </SentryRow>
                            );
                        })
                    }

                    <Button onClick={() => append(emptyIndividual(fields?.at(-1) ? fields.at(-1).itemId + 1 : 1))}>
                        <FaPlus /> Adicionar indivíduo
                    </Button>
                </Card.Body>
            </Card>

            <ModalReverseGeocoding
                open={addressMap.open}
                setOpen={(open) => setAddressMap((current) => ({open, index: open ? current.index : null}))}
                onConfirm={confirmAddressLocation}
                address={addressMap.index === null ? {} : {
                    ...watch(`individualList.${addressMap.index}.address`),
                    latitude: watch(`individualList.${addressMap.index}.address.lat`),
                    longitude: watch(`individualList.${addressMap.index}.address.lng`),
                }}
                showConfirmLocationAndAddress={false}
            />

            <Modal overflow={true} isCloseClick={false} open={articleModal.open}
                   setOpen={closeArticleModal} title={"Artigo"} className={"w-3/12"}>
                <Modal.Body>
                    <SentryRow>
                        <InputDateTime
                            label={"Data"}
                            selected={articleModal.data.date}
                            onChange={(date) => setArticleModal((current) => ({
                                ...current,
                                data: {...current.data, date: date ? format(date, "yyyy-MM-dd") : null},
                            }))}
                            classNameWrapper={"col-span-12"}
                            dateFormat={"L"}
                        />
                        <InputForm
                            label={"Boletim"}
                            value={articleModal.data.bo ?? ""}
                            onChange={({target: {value}}) => setArticleModal((current) => ({
                                ...current,
                                data: {...current.data, bo: value},
                            }))}
                            classNameWrapper={"col-span-12"}
                        />
                        <SelectComponent
                            classNameWrapper={"col-span-12"}
                            value={systemNatures.find((option) => option.value === articleModal.data.article) ?? optionsUtils.getValue(articleModal.data.article)}
                            options={systemNatures}
                            label={"Natureza *"}
                            onChange={(selected) => setArticleModal((current) => ({
                                ...current,
                                data: {...current.data, article: selected?.value ?? ""},
                            }))}
                        />
                        <InputFile
                            file={articleModalFile}
                            setFile={setArticleFile}
                            removeFile={removeArticleFile}
                            title={"Anexar arquivo"}
                            titleWithFile={"Visualizar anexo"}
                            className={"col-span-6"}
                        />
                    </SentryRow>
                </Modal.Body>
                <Modal.Footer>
                    <Button type={"button"} onClick={saveArticle}>Confirmar</Button>
                </Modal.Footer>
            </Modal>

            <Modal overflow={true} isCloseClick={false} open={individualRegionModal.open}
                   setOpen={closeIndividualRegionModal} title={"Característica"} className={"w-4/12"}>
                <Modal.Body>
                    <SentryRow>
                        <SelectComponent
                            classNameWrapper={"col-span-12"}
                            value={individualRegions?.find((option) => option.value === individualRegionModal.data.local) ?? null}
                            options={individualRegions ?? []}
                            label={individualRegionModal.allowNullLocal ? "Local" : "Local *"}
                            onChange={(selected) => setIndividualRegionModal((current) => ({
                                ...current,
                                data: {...current.data, local: selected?.value ?? ""},
                            }))}
                        />
                        <SelectComponent
                            classNameWrapper={"col-span-12"}
                            value={individualRegionDetails?.find((option) => option.value === individualRegionModal.data.type) ?? null}
                            options={individualRegionDetails ?? []}
                            label={"Tipo *"}
                            onChange={(selected) => setIndividualRegionModal((current) => ({
                                ...current,
                                data: {...current.data, type: selected?.value ?? ""},
                            }))}
                        />
                        <InputTextarea
                            className={"col-span-12"}
                            textAreaClass={"h-24"}
                            label={"Descrição *"}
                            value={individualRegionModal.data.description}
                            onChange={({target: {value}}) => setIndividualRegionModal((current) => ({
                                ...current,
                                data: {...current.data, description: value},
                            }))}
                        />
                    </SentryRow>
                </Modal.Body>
                <Modal.Footer>
                    <Button type={"button"} onClick={saveIndividualRegion}>Confirmar</Button>
                </Modal.Footer>
            </Modal>

        </>
    );
};

export default BoFormIndividual;
